import {collection, doc, onSnapshot, runTransaction, Unsubscribe} from 'firebase/firestore';
import {db} from '../firebaseConfig';
import {IFirestoreCalendarConfig, IFirestoreProductionResource, IProductionZone} from './interfaces';
import {firestoreCollections, firestoreDocumentPaths} from './firestoreConstants';

export function normalizeProductionResourceSortOrder(
  resources: IFirestoreProductionResource[]
): IFirestoreProductionResource[] {
  let activeResourceIndex = 0;

  return resources.map((resource) => {
    if (isProductionResourceArchived(resource)) {
      return resource;
    }

    activeResourceIndex += 1;
    return {
      ...resource,
      sortOrder: activeResourceIndex,
    };
  });
}

export function isProductionResourceArchived(resource: IFirestoreProductionResource): boolean {
  return resource.archivedAt != null;
}

export function getActiveProductionResources(
  resources: IFirestoreProductionResource[]
): IFirestoreProductionResource[] {
  return resources.filter((resource) => !isProductionResourceArchived(resource));
}

export function createProductionResourceId(): string {
  return doc(collection(db, firestoreCollections.productionResourceIds)).id;
}

export function productionResourceBelongsToZone(
  resource: IFirestoreProductionResource,
  productionZone: IProductionZone
): boolean {
  return resource.productionZone.path === productionZone.ref.path;
}

export function subscribeToProductionResources(
  onResourcesChange: (resources: IFirestoreProductionResource[]) => void,
  onError: (error: Error) => void
): Unsubscribe {
  const calendarConfigRef = doc(db, firestoreDocumentPaths.calendar);

  return onSnapshot(
    calendarConfigRef,
    (snapshot) => {
      const config = snapshot.exists()
        ? snapshot.data() as IFirestoreCalendarConfig
        : {};
      onResourcesChange(config.productionResources ?? []);
    },
    onError
  );
}

export async function saveProductionResourcesForZone(
  productionZone: IProductionZone,
  resourcesForZone: IFirestoreProductionResource[]
): Promise<void> {
  const calendarConfigRef = doc(db, firestoreDocumentPaths.calendar);

  await runTransaction(db, async (transaction) => {
    const calendarConfigSnapshot = await transaction.get(calendarConfigRef);
    const calendarConfig = calendarConfigSnapshot.exists()
      ? calendarConfigSnapshot.data() as IFirestoreCalendarConfig
      : {};
    const otherResources = (calendarConfig.productionResources ?? []).filter(
      (resource) => !productionResourceBelongsToZone(resource, productionZone)
    );
    const productionResources = normalizeProductionResourceSortOrder([
      ...otherResources,
      ...resourcesForZone.map((resource) => ({
        ...resource,
        productionZone: productionZone.ref,
      })),
    ]);

    transaction.set(
      calendarConfigRef,
      {productionResources},
      {merge: true}
    );
  });
}
